import { useState } from "react";
import {
  makeStyles,
  tokens,
  Button,
  Field,
  Input,
  Text,
  Card,
} from "@fluentui/react-components";
import { Add24Regular, Delete24Regular } from "@fluentui/react-icons";

const useStyles = makeStyles({
  wrap: { marginTop: "16px", display: "flex", flexDirection: "column", gap: "12px" },
  intro: { color: tokens.colorNeutralForeground3 },
  card: { display: "flex", flexDirection: "column", gap: "10px", padding: "12px" },
  row: { display: "flex", gap: "10px", flexWrap: "wrap", alignItems: "flex-end" },
  grow: { flexGrow: 1, minWidth: "200px" },
  select: { padding: "5px", borderRadius: "4px", border: `1px solid ${tokens.colorNeutralStroke1}` },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center" },
});

const FIELD_KEYS = ["jobTitle", "department", "phone", "mobile", "googleReviewUrl"];

// users object  <->  array for editing
const toRows = (users) =>
  Object.entries(users || {}).map(([email, v]) => ({ email, templateId: v.templateId || "", fields: v.fields || {} }));
const toObject = (rows) => {
  const users = {};
  rows.forEach((r) => {
    if (!r.email) return;
    const entry = {};
    if (r.templateId) entry.templateId = r.templateId;
    const fields = {};
    Object.entries(r.fields || {}).forEach(([k, val]) => { if (val) fields[k] = val; });
    if (Object.keys(fields).length) entry.fields = fields;
    users[r.email.toLowerCase()] = entry;
  });
  return users;
};

export default function OverridesTab({ value, templates, onChange }) {
  const styles = useStyles();
  // Local editing state so an in-progress, email-less row stays visible. Persisting
  // through toObject() (which drops blank rows) would otherwise delete a new row the
  // instant "Add user override" creates it, so nothing would ever appear.
  const [rows, setRows] = useState(() => toRows(value.users));

  const commit = (next) => {
    setRows(next);
    onChange({ ...value, users: toObject(next) });
  };
  const updateRow = (i, patch) => commit(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const updateField = (i, key, val) =>
    updateRow(i, { fields: { ...rows[i].fields, [key]: val } });
  const addRow = () => commit([...rows, { email: "", templateId: "", fields: {} }]);
  const removeRow = (i) => commit(rows.filter((_, idx) => idx !== i));

  return (
    <div className={styles.wrap}>
      <Text className={styles.intro}>
        Per-user overrides win over assignment rules. Force a specific template and/or override
        individual fields (e.g. a custom title or a personal review link) for one person.
      </Text>

      {rows.map((r, i) => (
        <Card key={i} className={styles.card}>
          <div className={styles.head}>
            <Text weight="semibold">{r.email || "(new user)"}</Text>
            <Button appearance="subtle" icon={<Delete24Regular />} onClick={() => removeRow(i)} />
          </div>
          <div className={styles.row}>
            <Field label="User email" className={styles.grow}>
              <Input value={r.email} onChange={(_, d) => updateRow(i, { email: d.value })} placeholder="name@davey.com" />
            </Field>
            <Field label="Force template">
              <select className={styles.select} value={r.templateId} onChange={(e) => updateRow(i, { templateId: e.target.value })}>
                <option value="">(use rules)</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </Field>
          </div>
          <div className={styles.row}>
            {FIELD_KEYS.map((key) => (
              <Field key={key} label={key}>
                <Input value={r.fields[key] || ""} onChange={(_, d) => updateField(i, key, d.value)} />
              </Field>
            ))}
          </div>
        </Card>
      ))}

      <Button icon={<Add24Regular />} onClick={addRow}>Add user override</Button>
    </div>
  );
}
